/**
 * Epic Analytics Dashboard Utilities
 *
 * Utility functions for data transformation, date calculations,
 * and other helper functions for the Epic Analytics Dashboard
 */

import {
  EpicAnalyticsFilters,
  EpicAnalyticsFiltersWithDates,
  QuarterConfig,
  YearRange,
} from "../types/epic-analytics-types";
import { QUARTER_CONFIGS, YEAR_RANGE } from "./epic-analytics-constants";

// ===== Date Utility Functions =====

/**
 * Convert filters to date range
 */
export function filtersToDateRange(
  filters: EpicAnalyticsFilters
): EpicAnalyticsFiltersWithDates {
  const { year, quarter } = filters;

  let startMonth: number;
  let endMonth: number;

  if (quarter === "all") {
    startMonth = 1;
    endMonth = 12;
  } else {
    const quarterConfig = QUARTER_CONFIGS.find((q) => q.value === quarter);
    if (!quarterConfig) {
      throw new Error(`Invalid quarter: ${quarter}`);
    }
    startMonth = quarterConfig.startMonth;
    endMonth = quarterConfig.endMonth;
  }

  const startDate = new Date(year, startMonth - 1, 1)
    .toISOString()
    .split("T")[0];
  const endDate = new Date(year, endMonth, 0).toISOString().split("T")[0];

  return {
    ...filters,
    startDate,
    endDate,
  };
}

/**
 * Get quarter from date
 */
export function getQuarterFromDate(date: string): 1 | 2 | 3 | 4 {
  const month = new Date(date).getMonth() + 1; // getMonth() returns 0-11

  if (month <= 3) return 1;
  if (month <= 6) return 2;
  if (month <= 9) return 3;
  return 4;
}

/**
 * Get year range for selector
 */
export function getYearRange(): YearRange {
  return YEAR_RANGE;
}

/**
 * Get available years as array
 */
export function getAvailableYears(): number[] {
  const { minYear, maxYear } = YEAR_RANGE;
  const years: number[] = [];

  for (let year = minYear; year <= maxYear; year++) {
    years.push(year);
  }

  return years;
}

/**
 * Format date for display
 */
export function formatDateForDisplay(date: string): string {
  return new Date(date).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

/**
 * Format date for chart (shorter format)
 */
export function formatDateForChart(date: string): string {
  return new Date(date).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
}

// ===== Validation Functions =====

/**
 * Validate epic analytics filters
 */
export function validateFilters(
  filters: Partial<EpicAnalyticsFilters>
): string[] {
  const errors: string[] = [];

  if (!filters.projectKey) {
    errors.push("Project is required");
  }

  if (filters.year !== undefined) {
    const { minYear, maxYear } = YEAR_RANGE;
    if (filters.year < minYear || filters.year > maxYear) {
      errors.push(`Year must be between ${minYear} and ${maxYear}`);
    }
  }

  if (filters.quarter !== undefined) {
    const validQuarters = ["all", 1, 2, 3, 4] as const;
    if (!validQuarters.includes(filters.quarter as any)) {
      errors.push("Invalid quarter selection");
    }
  }

  return errors;
}

/**
 * Check if filters are complete
 */
export function areFiltersComplete(
  filters: Partial<EpicAnalyticsFilters>
): filters is EpicAnalyticsFilters {
  return !!(
    filters.projectKey &&
    filters.year !== undefined &&
    filters.quarter !== undefined
  );
}

/**
 * Validate individual filter values
 */
export function validateFilterValue(
  field: keyof EpicAnalyticsFilters,
  value: any
): string | null {
  switch (field) {
    case "projectKey":
      if (!value || typeof value !== "string" || value.trim() === "") {
        return "Project key is required";
      }
      return null;

    case "year": {
      if (typeof value !== "number" || isNaN(value)) {
        return "Year must be a valid number";
      }
      const { minYear, maxYear } = YEAR_RANGE;
      if (value < minYear || value > maxYear) {
        return `Year must be between ${minYear} and ${maxYear}`;
      }
      return null;
    }

    case "quarter": {
      const validQuarters = ["all", 1, 2, 3, 4] as const;
      if (!validQuarters.includes(value as any)) {
        return 'Quarter must be "all" or 1, 2, 3, or 4';
      }
      return null;
    }

    default:
      return null;
  }
}

/**
 * Check if filter combination is valid
 */
export function validateFilterCombination(
  filters: Partial<EpicAnalyticsFilters>
): { isValid: boolean; errors: string[] } {
  const errors = validateFilters(filters);

  // Additional combination validations
  if (filters.year && filters.quarter) {
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentQuarter = getQuarterFromDate(
      currentDate.toISOString().split("T")[0]
    );

    // Warn if selecting future quarters
    if (
      filters.year > currentYear ||
      (filters.year === currentYear &&
        filters.quarter !== "all" &&
        filters.quarter > currentQuarter)
    ) {
      errors.push(
        "Selected time period is in the future and may not have data"
      );
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
}

// ===== Data Transformation Functions =====

/**
 * Calculate cumulative points for timeline data
 */
export function calculateCumulativePoints(
  epics: Array<{
    completionDate: string;
    storyPoints: number;
    epicKey: string;
    title: string;
  }>
): Array<{
  date: string;
  cumulativePoints: number;
  epicKey: string;
  epicTitle: string;
  pointsDelivered: number;
}> {
  // Sort epics by completion date
  const sortedEpics = [...epics].sort(
    (a, b) =>
      new Date(a.completionDate).getTime() -
      new Date(b.completionDate).getTime()
  );

  let cumulativePoints = 0;

  return sortedEpics.map((epic) => {
    cumulativePoints += epic.storyPoints;

    return {
      date: epic.completionDate,
      cumulativePoints,
      epicKey: epic.epicKey,
      epicTitle: epic.title,
      pointsDelivered: epic.storyPoints,
    };
  });
}

/**
 * Transform epics to bubble chart data
 */
export function transformToBubbleData(
  epics: Array<{
    epicKey: string;
    title: string;
    completionDate: string;
    storyPoints: number;
    bugCount: number;
  }>
): Array<{
  epicKey: string;
  epicTitle: string;
  completionDate: string;
  storyPoints: number;
  bugCount: number;
  x: number;
  y: number;
}> {
  return epics.map((epic, index) => ({
    epicKey: epic.epicKey,
    epicTitle: epic.title,
    completionDate: epic.completionDate,
    storyPoints: epic.storyPoints,
    bugCount: epic.bugCount,
    x: new Date(epic.completionDate).getTime(),
    y: Math.random() * 100 + 50, // Random Y position for visual separation
  }));
}

/**
 * Calculate summary metrics
 */
export function calculateSummaryMetrics(
  analyzedEpics: Array<{ storyPoints: number; bugCount: number }>,
  unanalyzedCount: number
): {
  totalEpics: number;
  totalPoints: number;
  totalBugs: number;
  unanalyzedEpics: number;
} {
  const totalPoints = analyzedEpics.reduce(
    (sum, epic) => sum + epic.storyPoints,
    0
  );
  const totalBugs = analyzedEpics.reduce((sum, epic) => sum + epic.bugCount, 0);

  return {
    totalEpics: analyzedEpics.length,
    totalPoints,
    totalBugs,
    unanalyzedEpics: unanalyzedCount,
  };
}

// ===== URL and Navigation Functions =====

/**
 * Generate URL for epic details page
 */
export function getEpicDetailsUrl(epicKey: string): string {
  return `/epics/${epicKey}`;
}

/**
 * Generate URL for epic analysis page with filters
 */
export function getEpicAnalysisUrl(filters: EpicAnalyticsFilters): string {
  // Convert year/quarter to date range for the analysis page
  const filtersWithDates = filtersToDateRange(filters);

  const params = new URLSearchParams({
    project: filters.projectKey,
    startDate: filtersWithDates.startDate,
    endDate: filtersWithDates.endDate,
  });

  return `/epics/analysis?${params.toString()}`;
}

/**
 * Parse filters from URL search params
 */
export function parseFiltersFromUrl(
  searchParams: URLSearchParams
): Partial<EpicAnalyticsFilters> {
  const filters: Partial<EpicAnalyticsFilters> = {};

  const project = searchParams.get("project");
  if (project) {
    filters.projectKey = project;
  }

  const year = searchParams.get("year");
  if (year) {
    const yearNum = parseInt(year, 10);
    if (!isNaN(yearNum)) {
      filters.year = yearNum;
    }
  }

  const quarter = searchParams.get("quarter");
  if (quarter) {
    if (quarter === "all") {
      filters.quarter = "all";
    } else {
      const quarterNum = parseInt(quarter, 10);
      if ([1, 2, 3, 4].includes(quarterNum)) {
        filters.quarter = quarterNum as 1 | 2 | 3 | 4;
      }
    }
  }

  return filters;
}

// ===== Chart Helper Functions =====

/**
 * Generate chart colors based on data
 */
export function generateChartColors(count: number): string[] {
  const baseColors = [
    "#3B82F6", // Blue
    "#10B981", // Emerald
    "#F59E0B", // Amber
    "#EF4444", // Red
    "#8B5CF6", // Violet
    "#06B6D4", // Cyan
    "#84CC16", // Lime
    "#F97316", // Orange
  ];

  const colors: string[] = [];
  for (let i = 0; i < count; i++) {
    colors.push(baseColors[i % baseColors.length]);
  }

  return colors;
}

/**
 * Format number for display
 */
export function formatNumber(num: number): string {
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1) + "M";
  }
  if (num >= 1000) {
    return (num / 1000).toFixed(1) + "K";
  }
  return num.toString();
}

/**
 * Format percentage for display
 */
export function formatPercentage(value: number, total: number): string {
  if (total === 0) return "0%";
  return Math.round((value / total) * 100) + "%";
}

// ===== Error Handling Functions =====

/**
 * Check if error is network related
 */
export function isNetworkError(error: any): boolean {
  return (
    error?.name === "NetworkError" ||
    error?.message?.includes("fetch") ||
    error?.message?.includes("network")
  );
}

/**
 * Get user-friendly error message with enhanced context
 */
export function getUserFriendlyErrorMessage(
  error: any,
  context?: string
): string {
  const contextPrefix = context ? `${context}: ` : "";

  if (isNetworkError(error)) {
    return (
      contextPrefix +
      "Network connection error. Please check your internet connection and try again."
    );
  }

  if (error?.status === 400) {
    return (
      contextPrefix +
      "Invalid request. Please check your filter selections and try again."
    );
  }

  if (error?.status === 401) {
    return contextPrefix + "You are not authorized to access this data.";
  }

  if (error?.status === 403) {
    return contextPrefix + "You do not have permission to access this data.";
  }

  if (error?.status === 404) {
    return (
      contextPrefix + "Epic analytics data not found for the selected filters."
    );
  }

  if (error?.status === 408) {
    return contextPrefix + "Request timeout. Please try again.";
  }

  if (error?.status === 429) {
    return (
      contextPrefix + "Too many requests. Please wait a moment and try again."
    );
  }

  if (error?.status === 500) {
    return contextPrefix + "Server error occurred. Please try again later.";
  }

  if (error?.status >= 502 && error?.status <= 504) {
    return (
      contextPrefix + "Service temporarily unavailable. Please try again later."
    );
  }

  // Handle specific error messages
  if (error?.message) {
    const message = error.message.toLowerCase();

    if (message.includes("timeout")) {
      return (
        contextPrefix +
        "The request took too long to complete. Please try again."
      );
    }

    if (message.includes("validation") || message.includes("invalid")) {
      return (
        contextPrefix +
        "The provided information is invalid. Please check your selections and try again."
      );
    }

    if (message.includes("cors")) {
      return (
        contextPrefix +
        "Cross-origin request blocked. Please contact support if this persists."
      );
    }

    if (message.includes("parse") || message.includes("json")) {
      return (
        contextPrefix +
        "Invalid data format received from server. Please try again."
      );
    }

    if (message.includes("abort")) {
      return contextPrefix + "Request was cancelled. Please try again.";
    }
  }

  return (
    contextPrefix +
    (error?.message || "An unexpected error occurred. Please try again.")
  );
}

/**
 * Enhanced error reporting with additional context
 */
export function reportError(
  error: any,
  context: string,
  additionalInfo?: Record<string, any>
) {
  const errorReport = {
    message: error?.message || "Unknown error",
    stack: error?.stack,
    context,
    timestamp: new Date().toISOString(),
    userAgent: navigator.userAgent,
    url: window.location.href,
    additionalInfo,
  };

  // Log to console in development
  if (process.env.NODE_ENV === "development") {
    console.group(`🚨 Error Report: ${context}`);
    console.error("Error:", error);
    console.table(errorReport);
    console.groupEnd();
  }

  // In production, this could send to an error tracking service
  // Example: Sentry.captureException(error, { extra: errorReport })

  return errorReport;
}

/**
 * Retry utility with exponential backoff
 */
export async function retryWithBackoff<T>(
  fn: () => Promise<T>,
  maxRetries: number = 3,
  baseDelay: number = 1000
): Promise<T> {
  let lastError: any;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;

      if (attempt === maxRetries) {
        throw error;
      }

      // Exponential backoff with jitter
      const delay = baseDelay * Math.pow(2, attempt) + Math.random() * 1000;
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }

  throw lastError;
}
